import { AppLayout } from "@/components/layout";
import { useAuth } from "@/hooks/use-auth";
import { apiFetch } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Send, Users, Mail, Phone, Loader2, ChevronDown, ChevronUp, Search, X, Check, AlertTriangle } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";

interface OwnerMessage {
  id: string;
  gymId: string;
  senderId: string;
  audience: "ALL_TRAINERS" | "ALL_STUDENTS" | "SELECTED";
  channel: "EMAIL" | "SMS" | "BOTH";
  subject: string | null;
  body: string;
  recipientCount: number;
  emailsSent: number;
  smsSent: number;
  emailsFailed: number;
  smsFailed: number;
  recipientIds: string[] | null;
  createdAt: string;
}

interface GymMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone: string | null;
}

function audienceLabel(audience: string) {
  if (audience === "ALL_TRAINERS") return "All Trainers";
  if (audience === "ALL_STUDENTS") return "All Students";
  return "Selected";
}

function channelIcon(channel: string) {
  if (channel === "EMAIL") return <Mail className="w-3.5 h-3.5" />;
  if (channel === "SMS") return <Phone className="w-3.5 h-3.5" />;
  return <MessageSquare className="w-3.5 h-3.5" />;
}

type AudienceType = "ALL_TRAINERS" | "ALL_STUDENTS" | "SELECTED";

export default function AdminConversations() {
  const { user } = useAuth();
  const { toast } = useToast();

  const [messages, setMessages] = useState<OwnerMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showCompose, setShowCompose] = useState(false);

  const [form, setForm] = useState({
    audience: "ALL_STUDENTS" as AudienceType,
    channel: "EMAIL" as "EMAIL" | "SMS" | "BOTH",
    subject: "",
    body: "",
  });

  const [members, setMembers] = useState<GymMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [memberSearch, setMemberSearch] = useState("");
  const [memberLookup, setMemberLookup] = useState<Record<string, string>>({});
  const [lookupFailed, setLookupFailed] = useState(false);
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      const data = await apiFetch<OwnerMessage[]>("/admin/messages");
      setMessages(data);
      const hasSelected = data.some((m) => m.audience === "SELECTED" && m.recipientIds && m.recipientIds.length > 0);
      if (hasSelected && data[0]?.gymId) {
        try {
          const gymMembers = await apiFetch<GymMember[]>(`/admin/users/gym/${data[0].gymId}`);
          const lookup: Record<string, string> = {};
          for (const member of gymMembers) {
            lookup[member.id] = `${member.firstName} ${member.lastName}`;
          }
          setMemberLookup(lookup);
          setLookupFailed(false);
        } catch {
          setLookupFailed(true);
        }
      }
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.gymId) fetchMessages();
  }, [user?.gymId, fetchMessages]);

  useEffect(() => {
    if (form.audience === "SELECTED" && user?.gymId && members.length === 0) {
      setMembersLoading(true);
      apiFetch<GymMember[]>(`/admin/users/gym/${user.gymId}`)
        .then((data) => setMembers(data.filter((m) => m.role === "TRAINER" || m.role === "STUDENT")))
        .catch(() => setMembers([]))
        .finally(() => setMembersLoading(false));
    }
  }, [form.audience, user?.gymId, members.length]);

  function toggleMember(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const filteredMembers = members.filter((m) => {
    const q = memberSearch.toLowerCase();
    return (
      m.firstName.toLowerCase().includes(q) ||
      m.lastName.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q)
    );
  });

  function memberMissingContact(m: GymMember): boolean {
    if (form.channel === "SMS") return !m.phone;
    if (form.channel === "EMAIL") return !m.email;
    if (form.channel === "BOTH") return !m.phone || !m.email;
    return false;
  }

  const selectedMissingMembers =
    form.audience === "SELECTED"
      ? Array.from(selectedIds)
          .map((id) => members.find((m) => m.id === id))
          .filter((m): m is GymMember => !!m && memberMissingContact(m))
      : [];

  function handleAudienceChange(v: string) {
    setForm({ ...form, audience: v as AudienceType });
    setShowWarningConfirm(false);
    if (v !== "SELECTED") {
      setSelectedIds(new Set());
      setMemberSearch("");
    }
  }

  async function handleSend(confirmed = false) {
    if (!form.body.trim()) {
      toast({ title: "Message body is required", variant: "destructive" });
      return;
    }
    if ((form.channel === "EMAIL" || form.channel === "BOTH") && !form.subject.trim()) {
      toast({ title: "Subject is required for email messages", variant: "destructive" });
      return;
    }
    if (form.audience === "SELECTED" && selectedIds.size === 0) {
      toast({ title: "Please select at least one recipient", variant: "destructive" });
      return;
    }
    if (form.audience === "SELECTED" && selectedMissingMembers.length > 0 && !confirmed) {
      setShowWarningConfirm(true);
      return;
    }
    setShowWarningConfirm(false);
    setSending(true);
    try {
      const payload: Record<string, unknown> = {
        audience: form.audience,
        channel: form.channel,
        subject: form.channel !== "SMS" ? form.subject : undefined,
        body: form.body,
      };
      if (form.audience === "SELECTED") {
        payload.recipientIds = Array.from(selectedIds);
      }
      const result = await apiFetch<{ recipientCount: number; emailsSent: number; smsSent: number }>("/admin/messages", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      toast({
        title: "Message sent",
        description: `Delivered to ${result.recipientCount} recipient${result.recipientCount !== 1 ? "s" : ""}.`,
      });
      setForm({ audience: "ALL_STUDENTS", channel: "EMAIL", subject: "", body: "" });
      setSelectedIds(new Set());
      setMemberSearch("");
      setShowWarningConfirm(false);
      setShowCompose(false);
      fetchMessages();
    } catch (e: any) {
      toast({ title: "Failed to send", description: e.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-display font-bold text-white mb-2">Broadcast Messages</h1>
            <p className="text-muted-foreground text-lg">Broadcast messages to your trainers and students</p>
          </div>
          <Button
            onClick={() => setShowCompose(!showCompose)}
            className="bg-primary text-black font-bold"
          >
            <Send className="w-4 h-4 mr-2" />
            {showCompose ? "Close" : "New Message"}
          </Button>
        </div>

        {showCompose && (
          <Card className="glass-panel border-primary/30">
            <CardHeader>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Send className="w-5 h-5 text-primary" /> Compose Message
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Audience</Label>
                  <Select value={form.audience} onValueChange={handleAudienceChange}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL_STUDENTS">All Students</SelectItem>
                      <SelectItem value="ALL_TRAINERS">All Trainers</SelectItem>
                      <SelectItem value="SELECTED">Selected members</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white">Channel</Label>
                  <Select
                    value={form.channel}
                    onValueChange={(v) => { setForm({ ...form, channel: v as typeof form.channel }); setShowWarningConfirm(false); }}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EMAIL">Email</SelectItem>
                      <SelectItem value="SMS">SMS</SelectItem>
                      <SelectItem value="BOTH">Email + SMS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {form.audience === "SELECTED" && (
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-2">
                    Recipients
                    {selectedIds.size > 0 && (
                      <span className="text-xs text-primary font-semibold bg-primary/10 px-2 py-0.5 rounded-full">
                        {selectedIds.size} selected
                      </span>
                    )}
                  </Label>
                  <div className="rounded-lg border border-white/10 bg-white/5 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                      <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                      <input
                        type="text"
                        value={memberSearch}
                        onChange={(e) => setMemberSearch(e.target.value)}
                        placeholder="Search by name or email..."
                        className="flex-1 bg-transparent text-sm text-white placeholder:text-muted-foreground outline-none"
                      />
                      {memberSearch && (
                        <button onClick={() => setMemberSearch("")} className="text-muted-foreground hover:text-white">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="max-h-52 overflow-y-auto">
                      {membersLoading ? (
                        <div className="flex items-center justify-center py-6">
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                      ) : filteredMembers.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-6">
                          {memberSearch ? "No members match your search" : "No members found"}
                        </p>
                      ) : (
                        filteredMembers.map((m) => {
                          const selected = selectedIds.has(m.id);
                          const missingContact = memberMissingContact(m);
                          const missingLabel =
                            form.channel === "SMS"
                              ? "No phone"
                              : form.channel === "EMAIL"
                              ? "No email"
                              : !m.phone && !m.email
                              ? "No phone or email"
                              : !m.phone
                              ? "No phone"
                              : "No email";
                          return (
                            <button
                              key={m.id}
                              onClick={() => { toggleMember(m.id); setShowWarningConfirm(false); }}
                              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/5 ${selected ? "bg-primary/10" : ""}`}
                            >
                              <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${selected ? "bg-primary border-primary" : "border-white/30"}`}>
                                {selected && <Check className="w-3 h-3 text-black" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white font-medium truncate">
                                  {m.firstName} {m.lastName}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                              </div>
                              {missingContact && (
                                <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded font-medium bg-amber-500/15 text-amber-400 shrink-0">
                                  <AlertTriangle className="w-3 h-3" /> {missingLabel}
                                </span>
                              )}
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${m.role === "TRAINER" ? "bg-blue-500/20 text-blue-400" : "bg-green-500/20 text-green-400"}`}>
                                {m.role === "TRAINER" ? "Trainer" : "Student"}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(form.channel === "EMAIL" || form.channel === "BOTH") && (
                <div className="space-y-2">
                  <Label className="text-white">Subject</Label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Email subject"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-white">Message</Label>
                <Textarea
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  placeholder="Write your message here..."
                  rows={5}
                  className="bg-white/5 border-white/10 text-white resize-none"
                />
              </div>

              {showWarningConfirm && selectedMissingMembers.length > 0 && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-300">
                        {selectedMissingMembers.length} of {selectedIds.size} recipient{selectedIds.size !== 1 ? "s" : ""}{" "}
                        {selectedMissingMembers.length === 1 ? "has" : "have"} no{" "}
                        {form.channel === "SMS" ? "phone number" : form.channel === "EMAIL" ? "email address" : "required contact info"} on file
                      </p>
                      <p className="text-xs text-amber-400/80 mt-0.5">
                        {selectedMissingMembers.map((m) => `${m.firstName} ${m.lastName}`).join(", ")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        These recipients will be skipped. Send anyway?
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleSend(true)}
                      disabled={sending}
                      className="bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs"
                    >
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                      {sending ? "Sending..." : "Send anyway"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowWarningConfirm(false)}
                      className="text-muted-foreground text-xs"
                    >
                      Go back
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={() => handleSend()} disabled={sending || showWarningConfirm} className="bg-primary text-black font-bold">
                  <Send className="w-4 h-4 mr-2" />
                  {sending ? "Sending..." : "Send Message"}
                </Button>
                <Button variant="ghost" onClick={() => { setShowCompose(false); setShowWarningConfirm(false); }} className="text-muted-foreground">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
            <MessageSquare className="w-16 h-16 text-muted-foreground opacity-30" />
            <h2 className="text-xl font-bold text-white">No Messages Yet</h2>
            <p className="text-muted-foreground text-center max-w-md">
              Use the "New Message" button to broadcast messages to your trainers and students.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Message History</h2>
            {messages.map((msg) => (
              <MessageCard key={msg.id} message={msg} memberLookup={memberLookup} lookupFailed={lookupFailed} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function MessageCard({ message, memberLookup, lookupFailed }: { message: OwnerMessage; memberLookup: Record<string, string>; lookupFailed: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const [showAllRecipients, setShowAllRecipients] = useState(false);

  const recipientNames =
    message.audience === "SELECTED" && !lookupFailed && message.recipientIds && message.recipientIds.length > 0
      ? message.recipientIds.map((id) => memberLookup[id] ?? "Former member")
      : null;

  const VISIBLE_LIMIT = 3;
  const visibleNames = recipientNames
    ? showAllRecipients
      ? recipientNames
      : recipientNames.slice(0, VISIBLE_LIMIT)
    : null;
  const hiddenCount = recipientNames ? Math.max(0, recipientNames.length - VISIBLE_LIMIT) : 0;

  return (
    <Card className="glass-panel border-white/5">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
                <Users className="w-3 h-3" /> {audienceLabel(message.audience)}
                {(message.audience === "ALL_TRAINERS" || message.audience === "ALL_STUDENTS") && message.recipientCount > 0 && (
                  <span className="text-primary/70 font-normal">· {message.recipientCount} sent</span>
                )}
              </span>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-white/5 text-muted-foreground font-medium">
                {channelIcon(message.channel)} {message.channel}
              </span>
              <span className="text-xs text-muted-foreground">
                {new Date(message.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
              </span>
            </div>

            {message.audience === "SELECTED" && lookupFailed && (
              <p className="text-xs text-muted-foreground mb-1">Recipient names unavailable</p>
            )}

            {visibleNames && (
              <div className="flex flex-wrap gap-1 mb-2" aria-label="recipients">
                {visibleNames.map((name, i) => (
                  <span
                    key={i}
                    className="text-xs px-1.5 py-0.5 rounded bg-white/5 text-muted-foreground border border-white/5"
                  >
                    {name}
                  </span>
                ))}
                {!showAllRecipients && hiddenCount > 0 && (
                  <button
                    onClick={() => setShowAllRecipients(true)}
                    className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors"
                    aria-label={`Show all ${recipientNames!.length} recipients`}
                  >
                    +{hiddenCount} more
                  </button>
                )}
                {showAllRecipients && hiddenCount > 0 && (
                  <button
                    onClick={() => setShowAllRecipients(false)}
                    className="text-xs px-1.5 py-0.5 rounded bg-white/5 text-muted-foreground border border-white/5 hover:bg-white/10 transition-colors"
                    aria-label="Show fewer recipients"
                  >
                    Show less
                  </button>
                )}
              </div>
            )}

            {message.subject && (
              <p className="text-sm font-semibold text-white mb-1">{message.subject}</p>
            )}

            <p className={`text-sm text-muted-foreground ${!expanded ? "line-clamp-2" : ""}`}>
              {message.body}
            </p>

            {message.body.length > 120 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-primary mt-1 flex items-center gap-1 hover:underline"
              >
                {expanded ? <><ChevronUp className="w-3 h-3" /> Show less</> : <><ChevronDown className="w-3 h-3" /> Show more</>}
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground shrink-0 sm:text-right">
            <div className="flex flex-col items-end gap-1">
              <span className="text-white font-semibold">{message.recipientCount} recipients</span>
              {message.emailsSent > 0 && (
                <span className="text-green-400">{message.emailsSent} email{message.emailsSent !== 1 ? "s" : ""} sent</span>
              )}
              {message.smsSent > 0 && (
                <span className="text-green-400">{message.smsSent} SMS sent</span>
              )}
              {(message.emailsFailed > 0 || message.smsFailed > 0) && (
                <span className="text-red-400">{message.emailsFailed + message.smsFailed} failed</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
